import { DownloadOutlined, LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import React, { useEffect, useRef, useState } from "react";
import "./index.css";
import { getReviewTasks } from "../../pages/applications/applicationDetail/tabs/dynamic/review/service";
import { isNullOrEmptyString, isNullOrUndefinedOrEmpty } from "../../common/utils";
import { errorMsg, successMsg } from "../simpleMessageAlert";
import { ReviewAction, sendReviewEvent } from "../matomoClient";
import { TooltipMessage } from "../../common/data/TooltipMsg";
import { AlertMessage } from "../../common/data/AlertMsg";
import AuthZButton from "../AuthzButton";
import { Tooltip } from "antd";
import { iif } from "../../common/utils/functions";

enum ReviewTaskStatus {
  DRAFT = "Saved",
  SUBMITTED = "Submitted",
}

const DecisionsPriority = {
  "not reviewed": 1,
  "no change needed": 2,
  "needs adjustment": 3,
  "needs removal": 4,
};

const ReviewTaskStatusPriority = {
  "saved": 1,
  "submitted": 2,
};

function DownloadReport({ reviewId, appId, reviewName, isSimpleButton = false, exportAssignedTasks = false }) {
  const csvDownloadRef = useRef<CSVLink>();

  const [headers, setHeaders] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const [readyToDownload, setReadyToDownload] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [timeOfDownload, setTimeOfDownload] = useState(getDate());

  function paddingZero(val) {
    if (val < 10) {
      return "0" + val;
    } else {
      return val;
    }
  }

  function getDate() {
    const date = new Date();
    const dateWithoutTime = `${date.getFullYear()}${paddingZero(date.getMonth() + 1)}${paddingZero(date.getDate())}`;
    const time = `${paddingZero(date.getHours())}${paddingZero(date.getMinutes())}${paddingZero(
      date.getSeconds(),
    )}`;
    return `${dateWithoutTime}${time}`;
  }

  function customizeColumns(customColumns) {
    const modifiedColumns = [...customColumns];
    /* istanbul ignore next */
    const indexOfTicketId = modifiedColumns.findIndex(({ label }) => {
      return label === "Ticket ID";
    });
    const indexOfReviewDecisionsAndReasoningColumn = modifiedColumns.findIndex(({ label }) => {
      return label === "Review decisions & Reasoning";
    });
    if (indexOfTicketId !== -1) {
      modifiedColumns.splice(indexOfTicketId + 1, 0, {
        label: "Ticket Status",
        key: "ticketStatus",
      });
      modifiedColumns.splice(indexOfReviewDecisionsAndReasoningColumn + 1, 0,
        {
          label: "Verification Status",
          key: "verificationStatus",
        },
      );
    }
    iif(function addLatestDecisionColumn() {
      const indexToInsertBefore = modifiedColumns.findIndex(item => item.label === "Verification Status");
      modifiedColumns.splice(indexToInsertBefore, 0, {
        label: "Decision",
        key: "decision",
      });
    });
    iif(function addReasonCategoryAndCommentsAsColumns() {
      const indexOfDecisionsColumn = modifiedColumns.findIndex(({ label }) => {
        return label === "Decision";
      });
      modifiedColumns.splice(indexOfDecisionsColumn + 1, 0, {
        label: "Decision Reason",
        key: "decisionReason",
      });
      modifiedColumns.splice(indexOfDecisionsColumn + 2, 0, {
        label: "Comments",
        key: "reviewerComment",
      });
    });
    iif(function addDecisionStatusColumn() {
      const indexOfCommentsColumn = modifiedColumns.findIndex(({ label }) => {
        return label === "Comments";
      });
      modifiedColumns.splice(indexOfCommentsColumn + 1, 0, {
        label: "Decision Status",
        key: "status",
      });
    });
    iif(function removeReviewDecisionsAndReasoningColumn() {
      modifiedColumns.splice(
        modifiedColumns.findIndex(({ label }) => label === "Review decisions & Reasoning"),
        1,
      );
    });
    iif(function removeDelegationColumn() {
      modifiedColumns.splice(modifiedColumns.findIndex(({ label }) => label === "Delegation"), 1);
    });
    return modifiedColumns;
  }

  function extractHeaderFromColumn(columns) {
    const modifiedColumns = columns.map((column) => {
      if (!isNullOrEmptyString(column.title)) {
        return {
          label: column.title,
          key: column.dataIndex,
        };
      }
      return null;
    });
    const customColumns = modifiedColumns.filter((item) => {
      return item != null;
    });
    return customizeColumns(customColumns);
  }

  function setHeaderAndDataSource(reviewTasks) {
    const decisionReasons = reviewTasks.decisionReasons;
    const dataSources = reviewTasks?.dataSource;
    let modifiedDataSources = dataSources?.map((data) => {
      const isTaskNotReviewed = !data.decision || data.decision === "(Not reviewed)";
      return {
        ...data,
        ticket: data?.reviewTaskTicket?.id,
        ticketStatus: data?.reviewTaskTicket?.status,
        /* istanbul ignore next */
        decisionReason: data.decisionReason
          ? decisionReasons.find(({ id }) => id === data.decisionReason.id)?.name
          : null,
        decision: isTaskNotReviewed ? "Not reviewed" : data.decision,
        status: data.status ? ReviewTaskStatus[data.status] : "",
      };
    });
    iif(function sortDatasourceBasedOnPriority() {
      const taskStatusPriority = (status: String) => status ? ReviewTaskStatusPriority[status.toLowerCase()] : 0;
      const decisionPriority = (decision: String) => decision ? DecisionsPriority[decision.toLowerCase()] : 0;

      modifiedDataSources = modifiedDataSources.sort((task1, task2) => {
        return (taskStatusPriority(task2.status) - taskStatusPriority(task1.status)) * 100 + (decisionPriority(task2.decision) - decisionPriority(task1.decision));
      });
    });
    const header = extractHeaderFromColumn(reviewTasks.columns);
    if (!isNullOrUndefinedOrEmpty(header)) {
      // @ts-ignore
      setHeaders(header);
    }
    if (!isNullOrUndefinedOrEmpty(modifiedDataSources)) {
      setDataSource(exportAssignedTasks
        ? modifiedDataSources.filter((task) => task.isAssignedToCurrentUser)
        : modifiedDataSources,
      );
    }
  }

  async function loadCsvDetails() {
    setButtonLoading(true);
    const reviewTasks = await getReviewTasks({ reviewId, appId });
    setHeaderAndDataSource(reviewTasks);
    successMsg(AlertMessage.downloadCSVReport.successMessage(reviewName));
  }

  async function downloadReportClickHandler() {
    try {
      await loadCsvDetails();
    } catch (err) {
      setButtonLoading(false);
      errorMsg(AlertMessage.downloadCSVReport.errorMessage(reviewName));
    }
  }

  useEffect(() => {
    if (!isNullOrUndefinedOrEmpty(headers)) {
      setReadyToDownload(true);
      setTimeOfDownload(getDate);
    }
  }, [dataSource]);

  useEffect(() => {
    if (readyToDownload) {
      csvDownloadRef.current.link.click();
      setReadyToDownload(false);
      setButtonLoading(false);
    }
  }, [readyToDownload]);

  function renderDownloadReportButton() {
    if (isSimpleButton) {
      return (
        <>
          {buttonLoading ? <LoadingOutlined className="loading-icon" /> :
            <Tooltip title={TooltipMessage.downloadCSVReport} placement={"bottom"}>
              <DownloadOutlined
                className="download-icon"
                onClick={async () => {
                  await downloadReportClickHandler();
                  sendReviewEvent(ReviewAction.DownloadReport, reviewName);
                }}
              />
            </Tooltip>
          }
        </>
      );
    }
    return (
      <div className="reviewHistoryActions">
        {/*@ts-ignore*/}
        <AuthZButton
          tooltipPlacement="right"
          tooltipTitle={TooltipMessage.downloadCSVReport}
          tooltipStyle={{ maxWidth: "6%" }}
          buttonType={"default"}
          icon={<DownloadOutlined />}
          buttonSize="small"
          buttonLoading={buttonLoading}
          onClick={async () => {
            await downloadReportClickHandler();
            sendReviewEvent(ReviewAction.DownloadReport, reviewName);
          }}
        >
          Review Report
        </AuthZButton>
      </div>
    );
  }

  function renderExportMyTasksButton() {
    return (
      // @ts-ignore
      <AuthZButton
        buttonType={"secondary"}
        authZButtonType={"default"}
        icon={<UploadOutlined />}
        buttonSize="medium"
        buttonLoading={buttonLoading}
        onClick={async () => {
          await downloadReportClickHandler();
          sendReviewEvent(ReviewAction.DownloadReport, reviewName);
        }}
      >
        Export My Tasks
      </AuthZButton>
    );
  }

  const filename = exportAssignedTasks
    ? `${reviewName}_myTasks_exportedAt_${timeOfDownload}.csv`
    : `${reviewName}_exportedAt_${timeOfDownload}.csv`;

  return (
    <>
      {exportAssignedTasks
        ? renderExportMyTasksButton()
        : renderDownloadReportButton()}
      <CSVLink
        ref={csvDownloadRef}
        data={dataSource}
        filename={filename}
        headers={headers}
        target="_blank"
      />
    </>
  );
}

export default DownloadReport;
